import { makeStyles } from "@material-ui/core";
import { ReactComponent as PencilBlueIcon } from "assets/icon/pencil_blue_icon.svg";
import { Alert, Box, Button, Modal, Paper, Typography } from "components";
import { MaterialIcon } from "components/common/MaterialIcon";
import { EnumMasterInsuranceState } from "constants/enums/master-insurance-state";
import { compose, withHooks, withTranslation } from "enhancers";
import styled from "styled-components";
import { AppColor } from "theme/app-color";
import {
  formatDate,
  gql,
  notifySuccess,
  paths,
  publishedAlert,
} from "utils/helper";
import { insuranceStepPath } from "../insuranceRoutes";
import InsuranceTableComponent from "./InsuranceTableComponent";

const useStyles = makeStyles((theme) => ({
  primaryButton: {
    backgroundColor: "#376FD0",
    borderColor: "#376FD0",
    color: "white",
    "&:hover": {
      backgroundColor: "#376FD0",
      borderColor: "#376FD0",
      color: "white",
    },
  },
  secondaryButton: {
    backgroundColor: "white",
    borderColor: "#376FD0",
    color: "#376FD0",
    "&:hover": {
      backgroundColor: "white",
      borderColor: "#376FD0",
      color: "#376FD0",
    },
  },
}));

const EditButton = styled(Button)`
  right: 16px;
  border-color: ${AppColor["Other/Info"]} !important;
  color: ${AppColor["Other/Info"]};
`;

const HeadIcon = styled(MaterialIcon)`
  font-size: 24px;
`;

const InsurancePackagePreviewPage = (props: any) => (
  <>
    <Paper p={4} mb={4}>
      <Box width="100%">
        <Typography variant="h4" mb={4}>
          {props.t(".title")}
        </Typography>
        <Alert severity="warning">
          <Typography variant="body1">{props.t(".warning")}</Typography>
        </Alert>
      </Box>
    </Paper>

    <Paper p={4} mb={4}>
      <Box width="100%">
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
            <Box display="flex" mr={2}>
              <HeadIcon
                name="WatchLaterOutlined"
                htmlColor={AppColor["Other/Info"]}
              />
            </Box>
            <Typography variant="h6" color="secondary">
              {props.t(".subtitle1")}
            </Typography>
          </Box>
          {props.canEdit && (
            <EditButton onClick={props.editInsurance} variant="outlined">
              <PencilBlueIcon style={{ marginRight: "10px" }} />
              {props.t(".edit")}
            </EditButton>
          )}
        </Box>

        <Box display="flex">
          <Box display="flex" flexDirection="column" flex={1}>
            <Typography variant="h6" mb={2}>
              {props.t(".startDate")}
            </Typography>

            <Typography variant="h6" mb={2}>
              {props.t(".endDate")}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" flex={1}>
            <Typography variant="body1" mb={2}>
              {props.protectionStartDate || "-"}
            </Typography>

            <Typography variant="body1" mb={2}>
              {props.protectionEndDate || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>

    <Paper p={4} mb={4}>
      <Box width="100%">
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
            <Box display="flex" mr={2}>
              <HeadIcon
                name="PeopleOutlined"
                htmlColor={AppColor["Other/Info"]}
              />
            </Box>
            <Typography variant="body2" color="secondary">
              {props.t(".subtitle2")}
            </Typography>
          </Box>
          {props.canEdit && (
            <EditButton onClick={props.editPackage} variant="outlined">
              <PencilBlueIcon style={{ marginRight: "10px" }} />
              {props.t(".edit")}
            </EditButton>
          )}
        </Box>

        <Box>
          <InsuranceTableComponent />
        </Box>
      </Box>
    </Paper>

    <Paper p={4}>
      <Box width="100%">
        <Typography variant="h4" mb={4}>
          {props.t(".title")}
        </Typography>
        <Alert severity="warning">
          <Typography variant="body1">{props.t(".warning")}</Typography>
        </Alert>
        {props.canEdit && (
          <Button color="primary" mt={6} onClick={props.handleClickSaveAndNext}>
            {props.t(props.switchPublishState.submitButton)}
          </Button>
        )}
      </Box>
    </Paper>
  </>
);

export const API = {
  FETCH_INSURANCE_DETAIL: gql`
    query FETCH_INSURANCE_DETAIL($id: String!) {
      masterInsuranceDetail(id: $id) {
        id
        year
        state
        subState
        companyName
        protectionStartDate
        protectionEndDate
        insurances {
          id
          nameTh
          nameEn
          premium
          remarkTh
          remarkEn
          description
          insuranceTypeId
        }
        insurancePackages {
          id
          nameTh
          nameEn
          remarkTh
          remarkEn
          insurancesInsurancePackages {
            id
            insurancePlanId
            packageId
          }
        }
      }
    }
  `,
  PUBLISH_INSURANCE: gql`
    mutation PUBLISH_INSURANCE($id: String!) {
      publishInsurance(id: $id) {
        id
        year
        state
      }
    }
  `,
};

const enhancer = compose(
  withTranslation({ prefix: "pages.main.insurance.InsurancePreview" }),
  withHooks((props: any, hooks: any) => {
    const {
      useMemo,
      useCallback,
      useMutation,
      useParams,
      useQuery,
      useDataTranslation,
    } = hooks;
    const { t } = props;

    const classes = useStyles();
    const { id: insuranceId } = useParams();

    const { data: masterInsurancePlansQry } = useQuery(
      API.FETCH_INSURANCE_DETAIL,
      {
        variables: { id: insuranceId },
      }
    );

    const masterInsurancePlans = useDataTranslation(
      masterInsurancePlansQry?.masterInsuranceDetail
    );

    const [publishInsurance] = useMutation(API.PUBLISH_INSURANCE, {
      skipSetError: true,
      onError: () => {
        publishedAlert();
      },
      onCompleted: () => {
        Modal.close();
        paths.insurancePath().push();
        notifySuccess(t(".success"));
      },
    });

    const editInsurance = useCallback(() => {
      insuranceStepPath.insuranceDetail(insuranceId).push();
    }, [insuranceId]);

    const editPackage = useCallback(() => {
      insuranceStepPath.insurancePackage(insuranceId).push();
    }, [insuranceId]);

    const canEdit = useMemo(
      () =>
        EnumMasterInsuranceState.published !== masterInsurancePlans?.state
          ? true
          : false,
      [masterInsurancePlans]
    );

    const switchPublishState = useMemo(() => {
      if (EnumMasterInsuranceState.draft === masterInsurancePlans?.state) {
        return {
          submitButton: ".publish",
          modalTitle: ".modalTitle",
          modalWarning: ".warning",
        };
      } else {
        return {
          submitButton: ".rePublish",
          modalTitle: ".rePublishModalTitleWarning",
          modalWarning: ".rePublishModalWarning",
        };
      }
    }, [masterInsurancePlans]);

    const handleClickSaveAndNext = useCallback(() => {
      Modal.open({
        title: t(switchPublishState.modalTitle),
        children: (
          <>
            <div style={{ display: "flex", flexDirection: "row" }}>
              <Typography variant="body1" color={AppColor["Text/Dark Grey"]}>
                {t(switchPublishState.modalWarning)}
              </Typography>
            </div>
          </>
        ),
        cancelButtonLabel: t(".cancel"),
        okButtonLabel: t(switchPublishState.submitButton),
        onOk: async ({ close }: any) => {
          await publishInsurance({ variables: { id: insuranceId } });
        },
        okButtonColor: AppColor["Other/Info"],
      });
    }, [insuranceId, publishInsurance, t, switchPublishState]);

    const protectionStartDateFormatted = useMemo(
      () => formatDate(masterInsurancePlans?.protectionStartDate, "dd/MM/yyy"),
      [masterInsurancePlans]
    );

    const protectionEndDateFormatted = useMemo(
      () => formatDate(masterInsurancePlans?.protectionEndDate, "dd/MM/yyy"),
      [masterInsurancePlans]
    );

    return {
      classes,
      editInsurance,
      editPackage,
      handleClickSaveAndNext,
      switchPublishState,
      canEdit,
      protectionStartDate: protectionStartDateFormatted,
      protectionEndDate: protectionEndDateFormatted,
    };
  })
);

export default enhancer(InsurancePackagePreviewPage);
